import type { ProductCard } from "types/product";

import type { ProductCardFragment } from "types/storefront.generated";

import { ProductBadgeResolver } from "./ProductBadgeResolver";

/**
 * Resolve product card props from a product card fragment.
 */
export class ProductCardResolver {
  /**
   * Is sale enabled for the current customer?
   * This prompts the resolver to use the sale price if available.
   */
  protected enableSale: boolean;

  /**
   * Product from Shopify.
   */
  protected shopifyProduct: ProductCardFragment;

  /**
   * Product badge resolver.
   */
  protected productBadgeResolver: ProductBadgeResolver;

  constructor({
    enableSale,
    shopifyProduct,
    productBadgeResolver,
  }: {
    enableSale: ProductCardResolver["enableSale"];
    shopifyProduct: ProductCardResolver["shopifyProduct"];
    productBadgeResolver: ProductCardResolver["productBadgeResolver"];
  }) {
    this.enableSale = enableSale;
    this.shopifyProduct = shopifyProduct;
    this.productBadgeResolver = productBadgeResolver;
  }

  resolve(): ProductCard {
    const image = this.shopifyProduct.images.nodes[0];
    const hoverImage = this.shopifyProduct.images.nodes[1];

    const color = this.shopifyProduct.options.find((option) =>
      ["color", "colour"].includes(option.name.toLowerCase()),
    )?.optionValues?.[0]?.name;

    const styleName = this.shopifyProduct.styleNameMetafield?.value;

    return {
      title: styleName ?? this.shopifyProduct.title,
      subtitle: color ?? null,
      handle: this.shopifyProduct.handle,
      image: image?.url ?? null,
      hoverImage: hoverImage?.url ?? null,
      badge: this.productBadgeResolver.resolve(),
      ...this._resolvePrices(),
    };
  }

  private _resolvePrices(): Pick<ProductCard, "price" | "compareAtPrice"> {
    // Resolve the compare at price.
    const compareAtPrice = () => {
      const price = this.shopifyProduct.compareAtPriceRange.maxVariantPrice;
      if (!price || !price.amount || "0.0" === price.amount) return null;
      return price;
    };

    // Determine the "ordinary" prices.
    const normalPrices = {
      compareAtPrice: compareAtPrice(),
      price: this.shopifyProduct.priceRange.maxVariantPrice,
    };

    // Not on sale, return ordinary prices from Shopify.
    if (!this.enableSale) {
      return normalPrices;
    }

    /**
     * Extract selected currency code from query.
     * This is automatically selected using @inContext on the query.
     */
    const currencyCode =
      this.shopifyProduct.priceRange.maxVariantPrice.currencyCode;

    // Attempt to find a sale price metafield for this currency.
    const salePriceMetafield = (this.shopifyProduct as any)[
      `salePrice${currencyCode}`
    ] as ProductCardFragment["salePriceAUD"] | undefined;

    // No sale price metafield found, return ordinary prices.
    if (!salePriceMetafield) return normalPrices;

    // Get the metafield value.
    const salePrice = salePriceMetafield.value;

    // No sale price metafield value found, return ordinary prices.
    if (!salePrice) return normalPrices;

    // Return sale price with the "normal" price as compare at price.
    return {
      price: { currencyCode, amount: salePrice },
      compareAtPrice: normalPrices.price,
    };
  }
}
